import {
    UnitPrefix,
    chooseFamiliarUnitProductSymbol,
    commandToLatex,
    genericSymbols,
    physicalQuantities,
    physicalQuantityStringToKeycapAside,
} from 'zycommon-web/utils/engineering-symbols';
import {
    alignButtonsInRows,
    eNotationInsertScenarios,
    getCommonNumberDeleteScenarios,
    getCommonNumberKeybindings,
    getCommonNumberKeyboardKeys,
    halfHeightSeparator,
    nonNumberInExponentScenario,
    scientificNotationInsertScenarios,
} from 'zycommon-web/utils/symbolic-tools/common-number';

// Units can be inserted via the virtual and physical keyboard
const virtualKey = /\\virtualkeyboard\{(\\\w+)\}/u;
const hyphenAfterSymbol = /\\(Q|P|S)[a-zA-Z]+-/u;
const hyphenAfterParens = /\\(Q|P|S)[a-zA-Z]+\)-/u;
const hyphenAfterMultiCharacterExponent = /\\(Q|P|S)[a-zA-Z]+\^\{[^}]*\}-/u;
const hyphenAfterSingleCharacterExponent = /\\(Q|P|S)[a-zA-Z]+\^\d-/u;
const unitProductSituations = [hyphenAfterSymbol, hyphenAfterParens, hyphenAfterMultiCharacterExponent, hyphenAfterSingleCharacterExponent];
const unitInsertScenarios = [
    // Minus sign (-) in units. Switch to \UP.
    {
        isScenarioPresent: value => unitProductSituations.some(situation => situation.test(value)),
        handleScenario: (editor, previousValue) => {
            // Two scenarios:
            // 1. Minus symbol added after a unit symbol. Ex: 1N -> 1N-
            // 2. Minus symbol added before unit symbol. Ex: 1-m -> 1N-m
            const match = unitProductSituations.map(situation => editor.value.match(situation)).find(Boolean);
            const signIndex = match.index + match[0].length - 1;
            const minusSignAdded = previousValue[signIndex - 1] !== '-';
            const positionsForMinusSign = 1;
            const positionsForUnitProduct = 5;
            const offset = minusSignAdded ? positionsForUnitProduct - positionsForMinusSign : 0;
            const oldPosition = editor.position;

            editor.value = `${editor.value.slice(0, signIndex)}\\UP${editor.value.slice(signIndex + 1)}`;
            editor.position = oldPosition + offset;
        },
    },

    // Automatically added a unit product symbol when a unit prefix or physical quantity is entered via the virtual keyboard.
    {
        isScenarioPresent: value => virtualKey.test(value),
        handleScenario: (editor, previousValue, previousCaretWasAtEnd, expectedAnswer) => {
            const keyMatch = editor.value.match(virtualKey);
            const [, symbol] = keyMatch;
            const beforeVirtual = editor.value.slice(0, keyMatch.index);
            const beforeVirtualIsPhysicalQuantity = /\\Q[a-zA-Z]+$/u.test(beforeVirtual);
            const unitProductSymbolSoFar = chooseFamiliarUnitProductSymbol({ actual: editor.value, expected: expectedAnswer });

            // Remove the virtual symbol
            editor.executeCommand('deleteBackward');
            editor.executeCommand('deleteBackward');

            let valueToInsert = '';

            // Insert a UnitProduct if previous item is a PhysicalQuantity
            if (beforeVirtualIsPhysicalQuantity) {
                valueToInsert = unitProductSymbolSoFar;
            }

            // Insert the symbol
            valueToInsert += symbol;
            editor.executeCommand(['insert', valueToInsert]);

            // The symbol may be in an exponent
            if (nonNumberInExponentScenario.isScenarioPresent(editor.value)) {
                nonNumberInExponentScenario.handleScenario(editor, previousValue, previousCaretWasAtEnd, expectedAnswer);
            }
        },
    },

    // Unit inserted after E notation with placeholder.
    {
        isScenarioPresent: value => /E\\(S|Q|P)/u.test(value),
        handleScenario: editor => {
            // Insert a placeholder before the unit.
            editor.executeCommand('moveToPreviousChar');
            editor.executeCommand(['insert', '\\placeholder{}']);

            // Move cursor to the end of the placeholder
            editor.executeCommand('moveToNextChar');
        },
    },
];

/**
 * Make a keycap for the given engineering symbol.
 * @function makeKeycapFromEngineeringSymbol
 * @param {EngineeringSymbol} symbol The given engineering symbol.
 * @returns {Object}
 */
function makeKeycapFromEngineeringSymbol(symbol) {
    const { abbreviation, command, exponent, latex } = symbol;
    let aside = physicalQuantityStringToKeycapAside[abbreviation];

    if (symbol instanceof UnitPrefix) {
        // MathML generated with MathJax 4 from LaTeX \[ 10^3 \], then added style="font-size: 14px;" to the <msup>
        aside = `<math xmlns="https://www.w3.org/1998/Math/MathML" display="block" data-semantic-structure="(2 0 1)">
<msup style="font-size: 14px;" data-semantic-type="superscript" data-semantic-role="integer" data-semantic-id="2" data-semantic-children="0,1" role="tree" data-semantic-owns="0 1" data-semantic-speech="10 cubed">
<mn data-semantic-type="number" data-semantic-role="integer" data-semantic-font="normal" data-semantic-annotation="clearspeak:simple" data-semantic-id="0" data-semantic-parent="2" aria-level="1" aria-posinset="1" aria-setsize="2" role="treeitem">10</mn>
<mn data-semantic-type="number" data-semantic-role="integer" data-semantic-font="normal" data-semantic-annotation="clearspeak:simple" data-semantic-id="1" data-semantic-parent="2" aria-level="1" aria-posinset="2" aria-setsize="2" role="treeitem">${exponent}</mn>
</msup>
</math>`;
    }

    return { aside, insert: `\\virtualkeyboard{\\${command}}`, latex };
}

/**
 * Get the delete scenarios for the Number and Units math live editor.
 * Note: The delete scenarios for Symbolic Number and Number and Units are the same.
 * @param {SymbolicShortAnswerQuestion} symbolicQuestion The symbolic short answer question containing the necessary tool-specific properties.
 * @param {boolean} symbolicQuestion.accept_E_notation Whether to accept E notation.
 * @param {boolean} symbolicQuestion.supportScientificNotation Whether to support scientific notation.
 * @returns {Array} The delete scenarios.
 */
export function getNumberAndUnitDeleteScenarios(symbolicQuestion) {
    const { accept_E_notation, supportScientificNotation } = symbolicQuestion;

    return getCommonNumberDeleteScenarios(accept_E_notation, supportScientificNotation);
}

/**
 * Get the insert scenarios for the Number and Units math live editor. The order in which the scenarios are added determines their precedence.
 * @param {SymbolicShortAnswerQuestion} symbolicQuestion The symbolic short answer question containing the necessary tool-specific properties.
 * @param {boolean} symbolicQuestion.accept_E_notation Whether to accept E notation.
 * @param {boolean} symbolicQuestion.supportScientificNotation Whether to support scientific notation.
 * @returns {Array} The insert scenarios.
 */
export function getNumberAndUnitInsertScenarios(symbolicQuestion) {
    const { accept_E_notation, supportScientificNotation } = symbolicQuestion;
    const insertScenarios = [];

    if (accept_E_notation) {
        insertScenarios.push(...eNotationInsertScenarios);
    }
    if (supportScientificNotation) {
        insertScenarios.push(...scientificNotationInsertScenarios);
    }
    insertScenarios.push(...unitInsertScenarios, nonNumberInExponentScenario);

    return insertScenarios;
}

/**
 * Get the Number and Unit macros for the math live editor.
 * @param {Object} currentMacros The current macros for the math live editor.
 * @returns {Object} The functions macros.
 */
export function getNumberAndUnitMacros(currentMacros) {
    return {
        ...currentMacros,
        ...commandToLatex,
        UP: '{\\text{-}}',
        Negate: '-',
        Keyboardx: 'x',
    };
}

/**
 * Get the keybindings for the Number and Unit math live editor.
 * @param {SymbolicShortAnswerQuestion} symbolicQuestion The symbolic short answer question containing the necessary tool-specific properties.
 * @param {boolean} symbolicQuestion.accept_E_notation Whether to accept E notation.
 * @param {Array} symbolicQuestion.physical_quantities The physical quantities to include in the virtual keyboard.
 * @param {boolean} symbolicQuestion.supportScientificNotation Whether to support scientific notation.
 * @returns {Array} The Number and Unit keybindings.
 */
export function getNumberAndUnitKeybindings(symbolicQuestion) {
    const { accept_E_notation, physical_quantities, supportScientificNotation } = symbolicQuestion;
    const keybindings = getCommonNumberKeybindings(accept_E_notation, supportScientificNotation);

    // Add keybindings for unit prefixes
    // Ohm is Ω, so prefer a keybinding. 'o' is a reasonable candidate. But, 'o' is also used in mol.
    // So, include keybind for 'o' to ohm if ohm is in the virtual keyboard and mol is not.
    // Same for Celsius as °C, so bind to 'C', but 'C' also is Coulomb.
    // Same for mu, so bind to 'u', but 'u' is also used in 'Btu'.
    const ambiguousPairs = [
        ['Ω', 'mol'],
        ['°C', 'C'],
        ['μ', 'Btu'],
    ];
    const disambiguousKeybindings = ambiguousPairs
        .filter(([first, second]) => physical_quantities.includes(first) && !physical_quantities.includes(second))
        .map(([first]) => {
            const { command, key } = physicalQuantities.find(({ abbreviation }) => abbreviation === first);

            return { key, command: ['insert', `\\${command}`] };
        });

    keybindings.push(...disambiguousKeybindings);

    // Add a generic symbol for each unused letter (upper and lower case)
    const engineeringSymbolKeybindings = genericSymbols
        .filter(({ key }) => !keybindings.some(binding => key === binding.key))
        .map(({ key, command }) => ({ key, command: ['insert', `\\${command}`] }));

    keybindings.push(...engineeringSymbolKeybindings);

    return keybindings;
}

/**
 * The Number and Unit virtual keyboard layout.
 * @param {Object} commonKeys The common keys used across different virtual keyboard layouts.
 * @param {SymbolicShortAnswerQuestion} symbolicQuestion The symbolic short answer question containing the necessary tool-specific properties.
 * @param {boolean} symbolicQuestion.accept_E_notation Whether to accept E notation.
 * @param {string} symbolicQuestion.expectedString The expected answer string.
 * @param {boolean} symbolicQuestion.hide_E_notation Whether to hide E notation key from the virtual keyboard.
 * @param {Array} symbolicQuestion.physicalQuantitiesInVirtualKeyboard The physical quantities to include in the virtual keyboard.
 * @param {boolean} symbolicQuestion.supportScientificNotation Whether to support scientific notation.
 * @param {Array} symbolicQuestion.unitPrefixesInVirtualKeyboard The unit prefixes to include in the virtual keyboard.
 * @returns {Array} The keyboard layout.
 */
export function getNumberAndUnitKeyboardLayout(commonKeys, symbolicQuestion) {
    const { deleteKeyCap, exponent, moveLeftKeyCap, moveRightKeyCap, nextStepKeyCap, separator } = commonKeys;
    const {
        accept_E_notation,
        expectedString,
        hide_E_notation,
        physicalQuantitiesInVirtualKeyboard,
        supportScientificNotation,
        unitPrefixesInVirtualKeyboard,
    } = symbolicQuestion;
    const rows = [];

    // Step 1: Get common keys (numbers, negate, decimal point, E notation, scientific notation)
    rows.push(...getCommonNumberKeyboardKeys(commonKeys, accept_E_notation, hide_E_notation, supportScientificNotation));

    // Step 2: Add optional units
    // Add row with label for Numbers and Units
    const makeLabel = label => ({ class: 'label half-height', label });

    rows.unshift([makeLabel('Numbers')]);

    // Align numbers
    alignButtonsInRows(rows);

    // Add padding (column) between numbers and units
    rows.forEach((row, index) => {
        row.push(index === 0 ? halfHeightSeparator : separator);
    });

    // Add Units label
    rows[0].push(makeLabel('Units'));

    // Add prefixes and quantities
    let indexForQuantities = 1;

    if (unitPrefixesInVirtualKeyboard.length) {
        rows[1].push(...unitPrefixesInVirtualKeyboard.map(makeKeycapFromEngineeringSymbol));
        indexForQuantities = 2;
    }

    rows[indexForQuantities].push(...physicalQuantitiesInVirtualKeyboard.map(makeKeycapFromEngineeringSymbol));

    // Add unit operators
    const multiplyLatex = chooseFamiliarUnitProductSymbol({ actual: '', expected: expectedString ?? '' });

    rows[indexForQuantities + 1].push(
        { aside: 'multiply', insert: multiplyLatex, latex: multiplyLatex === '\\UP' ? '{\\text{-}}' : multiplyLatex },
        { aside: 'divide', latex: '/' },
        { aside: 'exponent', latex: exponent },
    );

    // Step 3: Add control keys (move left, move right, Delete, Check)
    // eslint-disable-next-line no-magic-numbers
    rows[4].push(moveLeftKeyCap, moveRightKeyCap);
    rows[rows.length - 1].push(deleteKeyCap, nextStepKeyCap);

    // Step 4: Align rows
    alignButtonsInRows(rows);

    return rows;
}
